import type { ComponentPropsWithoutRef } from "react"
import { forwardRef, useCallback, useState } from "react"
import { Switch } from "@radix-ui/react-switch"

import type { Plan, PlanInterval } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/browser"
import { PlanData, PlansGrid, Row } from "@productlane/ui"

import type { Prices } from "./pricing-context"
import PlanHeader from "./plan-header"
import { PricingContext } from "./pricing-context"

const Headers = () => {
	return (
		<>
			<PlanHeader plan="starter">
				<PlanHeader.Name>Starter</PlanHeader.Name>
				<PlanHeader.Price />
				<PlanHeader.CheckoutButton />
			</PlanHeader>
			<PlanHeader plan="pro">
				<PlanHeader.Name>Pro</PlanHeader.Name>
				<PlanHeader.Price />
				<PlanHeader.CheckoutButton />
			</PlanHeader>
			<PlanHeader plan="scale">
				<PlanHeader.Name>Scale</PlanHeader.Name>
				<PlanHeader.Price />
				<PlanHeader.CheckoutButton />
			</PlanHeader>
		</>
	)
}

const SwitchBackground = forwardRef<
	HTMLSpanElement,
	ComponentPropsWithoutRef<"span"> & { checked: boolean }
>(({ className, checked, ...props }, ref) => {
	return (
		<span
			ref={ref}
			className={cn(
				"absolute top-1 h-[calc(100%-0.5rem)] w-[calc(50%-0.5rem)] rounded-md bg-white shadow-sm ring-1 ring-black/10 transition-all duration-200 ease-out dark:bg-gray-700 dark:shadow-black/25 dark:ring-white/15",
				checked ? "left-3 translate-x-full" : "left-1 translate-x-0",
				className,
			)}
			{...props}
		/>
	)
})

const Plans = (props: {
	activePlan: Plan
	prices: Prices
	isPublic: boolean
}) => {
	const [subInterval, setSubInterval] = useState<PlanInterval>("yearly")

	const onChange = useCallback(() => {
		setSubInterval(subInterval === "monthly" ? "yearly" : "monthly")
	}, [subInterval])

	const checked = subInterval === "monthly"

	const activeCol =
		props.activePlan === "starter"
			? "1"
			: props.activePlan === "pro"
				? "2"
				: props.activePlan === "scale"
					? "3"
					: undefined

	return (
		<PricingContext.Provider
			value={{
				subscriptionInterval: subInterval,
				prices: props.prices,
				isPublic: props.isPublic,
				activePlan: props.activePlan,
			}}
		>
			<PlansGrid activeCol={activeCol}>
				<Row className="sticky top-0 z-10 -mx-6 mt-4 rounded-2xl bg-white px-6 pb-4 lg:static lg:z-auto lg:mx-0 lg:h-[9.5rem] lg:rounded-none lg:bg-transparent lg:px-0 lg:pb-0 dark:bg-gray-900 dark:lg:bg-transparent">
					<Row.Header className="mb-6 flex justify-start border-b-0 lg:mb-0 lg:items-end">
						<Switch
							className="relative -mt-0.5 flex h-8 items-center whitespace-nowrap rounded-lg bg-gray-100 text-xs text-primary dark:bg-gray-800"
							checked={checked}
							onCheckedChange={onChange}
						>
							<div className={`z-1 w-24 select-none text-center font-medium`}>
								Yearly
							</div>
							<div className={`z-1 w-24 select-none text-center font-medium`}>
								Monthly
							</div>
							<SwitchBackground checked={checked} />
						</Switch>
					</Row.Header>
					<Headers />
				</Row>
				<PlanData />
				<Row className="mb-4 mt-12 border-b-0">
					<Row.Header className="opacity-0"></Row.Header>
					<PlanHeader plan="starter">
						<PlanHeader.CheckoutButton />
					</PlanHeader>
					<PlanHeader plan="pro">
						<PlanHeader.CheckoutButton />
					</PlanHeader>
					<PlanHeader plan="scale">
						<PlanHeader.CheckoutButton />
					</PlanHeader>
				</Row>
			</PlansGrid>
		</PricingContext.Provider>
	)
}

export default Plans
